/**
 * HeaderGrid - Grid-based header for Classic layout
 *
 * Uses the 12-column grid system for consistent responsive behavior.
 * This is the new version that uses CSS breakpoints instead of JS conditionals.
 *
 * Layout:
 * - Mobile (< 768px): Stacked - image full width, details below
 * - Tablet+ (≥ 768px): Side-by-side - image (8 cols) + details (4 cols)
 *
 * Uses headerMain/headerSidebar spans which switch earlier than main/sidebar.
 */
import { useState, useEffect, Fragment } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "next/link";
import moment from "moment-timezone";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { Section } from "@shared/components/application/layout/Section/Section.web";
import breakpoints from "@shared/theme/props/breakpoints";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { formatTimer, useTimer } from "@ui-kit/feedback/timer/useTimer";
import { domElementIds } from "@shared/constants/common";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import { gridTemplates, gridSpans, gridGaps } from "@shared/theme/props/grid";
import type { PageData } from "../../../adapters/types";

const { sm } = breakpoints;
const { headerDetailsId } = domElementIds;

interface HeaderProps {
  data: PageData;
}

export function Header({ data }: HeaderProps) {
  const { t } = useTranslation("purchases");
  const { header, ui } = data;

  // Sale countdown timer
  const saleStartsOnTz = header.saleStartsOn
    ? moment.tz(header.saleStartsOn, header.timezone || "").toDate()
    : new Date();
  const [timeRemaining] = useTimer(saleStartsOnTz);
  const { hours, minutes, seconds } = formatTimer(timeRemaining);

  // Hydration-safe date comparison
  const [saleStartsOnPassed, setSaleStartsOnPassed] = useState(true);
  useEffect(() => {
    if (header.saleStartsOn) {
      setSaleStartsOnPassed(
        Date.now() >= new Date(header.saleStartsOn).valueOf()
      );
    }
  }, [header.saleStartsOn]);

  const showSaleStartsOn = header.saleStartsOn && !saleStartsOnPassed;

  const orgUrl = header.organizer?.slug ? `/o/${header.organizer.slug}/` : null;

  const handleOrgClick = (e: React.MouseEvent) => {
    e.preventDefault();
    header.organizer.onContact?.();
  };

  // Image component
  const renderImage = () => {
    let src = formatRelativeStaticPath("images/default-banner.png");
    if (header.image) {
      src = header.image;
    }

    return (
      <Box
        overflow="hidden"
        borderRadius={{ base: "none", sm: "md" }}
        aspectRatio={2 / 1}
        w="100%"
        h="100%"
      >
        <CardImage
          src={src}
          width={466}
          height={233}
          alt={t("Event image for {{eventName}}", {
            eventName: header.title,
          })}
          title={t("Event image for {{eventName}}", {
            eventName: header.title,
          })}
          ariaLabel={t("Event image for {{eventName}}", {
            eventName: header.title,
          })}
          favoriteData={header.favoriteData}
          sizes={`
            (max-width: ${sm}px) 100vw,
            (min-width: ${sm}px) 66vw,
          `}
          useServerlessResize
          aspectRatio="2"
        />
      </Box>
    );
  };

  // Sale countdown component
  const renderSaleCountdown = () => {
    if (!showSaleStartsOn) return null;

    return (
      <Flex w="100%" direction="column" mb="lg" data-testid="sale-countdown">
        <Text as="h3" fontSize="xl" fontWeight="bold" color="purple.fg" mb="xs">
          {t("Sale starts in")}
        </Text>
        <Flex direction="row" w="100%" gap="xs">
          {[
            { value: hours, label: t("Hour", { count: Number(hours) }) },
            { value: minutes, label: t("Minute", { count: Number(minutes) }) },
            { value: seconds, label: t("Second", { count: Number(seconds) }) },
          ].map(({ value, label }) => (
            <Box
              key={label}
              flex="1"
              p="xs"
              textAlign="center"
              bg="purple.subtle"
              borderRadius="md"
            >
              <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
                {timeRemaining > 0 ? value : "0"}
              </Text>
              <Text color="purple.fg">{label}</Text>
            </Box>
          ))}
        </Flex>
      </Flex>
    );
  };

  // Status component - uses statuses from the adapter
  const visibleStatuses = header.statuses.filter((status) => status.visible);

  const renderStatus = () => {
    if (visibleStatuses.length === 0) return null;

    return (
      <Flex mt="xs" alignItems="center" gap="md" flexWrap="wrap">
        {visibleStatuses.map((status) => (
          <Fragment key={status.id}>{status.element}</Fragment>
        ))}
      </Flex>
    );
  };

  // Badge component - uses badges from the adapter
  const visibleBadges = header.badges.filter((badge) => badge.visible);

  const renderBadge = () => {
    if (visibleBadges.length === 0) return null;

    return (
      <SimpleGrid mt="xs" columns={{ base: 1, sm: 2 }} fontSize="sm" color="fg">
        {visibleBadges.map(({ id, dataTestId, icon, text }) => (
          <Flex
            key={id}
            mr="lg"
            p={{ base: "2xs", sm: "xs" }}
            alignItems="flex-start"
            data-testid={dataTestId}
          >
            {icon} {text}
          </Flex>
        ))}
      </SimpleGrid>
    );
  };

  if (!header.title) return null;

  return (
    <Section noPaddingMobile bg="bg">
      <Grid
        id={headerDetailsId}
        templateColumns={gridTemplates.default}
        gap={gridGaps.default}
        py={{ base: "none", sm: "xl" }}
        data-testid="header-grid"
      >
        {/* Image column - 12 mobile → 8 tablet → 4 desktop */}
        <GridItem colSpan={gridSpans.headerImage}>
          <Skeleton
            w="100%"
            h="100%"
            borderRadius={{ base: "none", sm: "md" }}
            isLoaded={!ui.isLoading}
          >
            <Box data-testid="event-image">{renderImage()}</Box>
          </Skeleton>
        </GridItem>

        {/* Details column - 12 mobile → 4 tablet → 8 desktop */}
        <GridItem colSpan={gridSpans.headerDetails}>
          <Flex
            direction="column"
            alignItems="flex-start"
            p={{ base: "md", sm: "none" }}
            data-testid="header-details"
          >
            {/* Sale countdown */}
            {renderSaleCountdown()}

            {/* Title */}
            <Skeleton
              w="100%"
              isLoaded={!ui.isLoading}
              mb={ui.isLoading ? "xs" : "none"}
            >
              <Text
                as="h1"
                fontSize={{ base: "xl", sm: "3xl" }}
                fontWeight="bold"
                color="fg"
                data-testid="title"
              >
                {header.title}
              </Text>
            </Skeleton>

            {/* Subtitle */}
            {header.subtitle && (
              <Text
                as="h2"
                fontSize={{ base: "md", sm: "lg" }}
                color="fg.muted"
                data-testid="subtitle"
              >
                {header.subtitle}
              </Text>
            )}

            {/* Organizer */}
            {header.organizer?.name && orgUrl && (
              <Text
                as="h3"
                fontSize={{ base: "sm", sm: "md" }}
                color="fg.muted"
                data-testid="organizer"
              >
                <Link href={orgUrl} onClick={handleOrgClick}>
                  {header.organizer.name}
                </Link>
              </Text>
            )}

            {/* Status badges */}
            {renderStatus()}

            {/* Event/membership badges */}
            <Skeleton isLoaded={!ui.isLoading} mt="sm">
              {renderBadge()}
            </Skeleton>
          </Flex>
        </GridItem>
      </Grid>
    </Section>
  );
}
